import type { Approval } from "@zworks/console-contracts";
import { Bell, CheckCheck, Inbox } from "lucide-react";
import * as React from "react";
import { Link } from "react-router";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface NotificationsMenuProps {
  projectId: string | null;
  waitingApprovals?: Approval[];
}

function formatRelativeTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).format(date);
  } catch {
    return "";
  }
}

function getStoredReadIds(projectId: string | null): Set<string> {
  if (!projectId) return new Set();
  try {
    const stored = localStorage.getItem(`zworks:notifications-read:${projectId}`);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

/**
 * Top bar notification menu.
 * Uses real console data (pending approvals waiting for caller).
 * Read state is stored in client storage as no server-side notification read endpoint exists.
 */
export function NotificationsMenu({ projectId, waitingApprovals = [] }: NotificationsMenuProps) {
  const [open, setOpen] = React.useState(false);
  const [readIds, setReadIds] = React.useState<Set<string>>(() => getStoredReadIds(projectId));
  const [prevProjectId, setPrevProjectId] = React.useState(projectId);

  if (projectId !== prevProjectId) {
    setPrevProjectId(projectId);
    setReadIds(getStoredReadIds(projectId));
  }

  const unreadApprovals = waitingApprovals.filter((approval) => !readIds.has(approval.id));
  const unreadCount = unreadApprovals.length;

  const handleMarkAllRead = () => {
    const nextReadIds = new Set(readIds);
    for (const approval of waitingApprovals) {
      nextReadIds.add(approval.id);
    }
    setReadIds(nextReadIds);
    if (projectId) {
      try {
        localStorage.setItem(
          `zworks:notifications-read:${projectId}`,
          JSON.stringify(Array.from(nextReadIds)),
        );
      } catch {
        // Storage could be unavailable or restricted.
      }
    }
  };

  const handleItemClick = (id: string) => {
    if (!readIds.has(id)) {
      const nextReadIds = new Set(readIds);
      nextReadIds.add(id);
      setReadIds(nextReadIds);
      if (projectId) {
        try {
          localStorage.setItem(
            `zworks:notifications-read:${projectId}`,
            JSON.stringify(Array.from(nextReadIds)),
          );
        } catch {
          // Ignore storage errors.
        }
      }
    }
    setOpen(false);
  };

  const triggerLabel =
    unreadCount > 0 ? `Notifications, ${unreadCount} unread` : "Notifications, none unread";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <div className="relative">
        <PopoverTrigger
          aria-label={triggerLabel}
          className="relative inline-flex size-11 items-center justify-center rounded-lg text-muted-foreground hover:bg-accent hover:text-foreground transition-colors md:size-8"
        >
          <Bell className="size-4" aria-hidden />
          {unreadCount > 0 && (
            <span
              aria-hidden
              className="absolute -top-1 -right-1 min-w-4 rounded-full border border-background bg-chart px-1 text-center font-mono text-xs font-bold text-primary-foreground leading-4 shadow-2xs"
            >
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </PopoverTrigger>

        <PopoverContent
          className="right-0 top-full mt-2 w-80 p-0 shadow-lg"
          aria-label="Notifications"
        >
          <div className="flex items-center justify-between border-b px-3.5 py-2.5">
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-foreground">Notifications</span>
              {unreadCount > 0 && (
                <span className="rounded-full bg-chart/10 px-1.5 py-0.2 text-xs font-mono font-medium text-accent-text border border-chart/30">
                  {unreadCount} unread
                </span>
              )}
            </div>
            {waitingApprovals.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-11 px-2 text-xs text-muted-foreground hover:text-foreground md:h-7"
                onClick={handleMarkAllRead}
              >
                <CheckCheck className="mr-1 size-3.5" aria-hidden />
                Mark all read
              </Button>
            )}
          </div>

          <div className="max-h-72 overflow-y-auto p-1 divide-y divide-border/40">
            {waitingApprovals.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-6 text-center">
                <Inbox className="size-8 text-muted-foreground/40 mb-2" aria-hidden />
                <p className="text-sm font-medium text-foreground">No notifications</p>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-[14rem]">
                  All caught up. When an approval or alert needs your attention, it will appear
                  here.
                </p>
              </div>
            ) : (
              waitingApprovals.map((approval) => {
                const isUnread = !readIds.has(approval.id);
                const time = formatRelativeTime(approval.requestedAt);
                return (
                  <Link
                    key={approval.id}
                    to={`/approvals/${approval.id}`}
                    onClick={() => handleItemClick(approval.id)}
                    className={cn(
                      "flex items-start gap-2.5 p-2.5 rounded-md hover:bg-muted/70 transition-colors text-left",
                      isUnread && "bg-muted/30",
                    )}
                  >
                    <span
                      aria-hidden
                      className={cn(
                        "size-2 rounded-full mt-1.5 shrink-0 transition-colors",
                        isUnread ? "bg-chart" : "bg-transparent",
                      )}
                    />
                    <div className="min-w-0 flex-1 space-y-0.5">
                      <p
                        className={cn(
                          "text-xs leading-snug",
                          isUnread
                            ? "font-semibold text-foreground"
                            : "font-normal text-muted-foreground",
                        )}
                      >
                        {approval.title || "Approval required"}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {approval.actionType} requested by {approval.requestedBy}
                        {time ? ` · ${time}` : ""}
                      </p>
                    </div>
                  </Link>
                );
              })
            )}
          </div>

          <div className="border-t bg-muted/30 px-3 py-1.5 text-xs text-muted-foreground">
            Read state is stored in this browser on this device.
          </div>
        </PopoverContent>
      </div>
    </Popover>
  );
}
